if (typeof Swiper === 'undefined') {
  console.error('Swiper library not loaded');
}

class ProductsList extends HTMLElement {
  constructor() {
    super();
    this.swiperInstance = null;
  }

  connectedCallback() {
    this.section = this.closest('.products-list');
    if (!this.section) return;

    this.filters = this.section.querySelectorAll('.products-list__filter');
    this.dropdown = this.section.querySelector('.products-list__filters-dropdown');
    this.productCards = this.section.querySelectorAll('.product-card');

    this.initSwiper();
    this.initFilters();
    this.initAddToCart();
  }

  initSwiper() {
    const swiperEl = this.section.querySelector('.products-list__swiper');

    if (!swiperEl) return;

    this.swiperInstance = new Swiper(swiperEl, {
      slidesPerView: 2,
      spaceBetween: 12,
      navigation: {
        nextEl: this.section.querySelector('.swiper-nav__arrow--next'),
        prevEl: this.section.querySelector('.swiper-nav__arrow--prev'),
      },
      pagination: {
        el: this.section.querySelector('.swiper-nav__pagination'),
        clickable: true,
      },
      breakpoints: {
        990: {
          slidesPerView: 4,
          spaceBetween: 18,
        }
      }
    });
  }

  initFilters() {
    this.filters.forEach(filter => {
      filter.addEventListener('click', (e) => {
        this.handleFilterClick(e.currentTarget);
      });
    });

    if (this.dropdown) {
      this.dropdown.addEventListener('change', (e) => {
        this.handleDropdownChange(e.currentTarget.value);
      });
    }

    // Apply initial filter (first active filter)
    const activeFilter = this.section.querySelector('.products-list__filter--active');
    if (activeFilter) {
      this.applyInitialFilter(activeFilter.dataset.filter);
    }
  }

  applyInitialFilter(filterValue) {
    // Hide slides that don't match the initial filter (no animation)
    const slides = this.section.querySelectorAll('.swiper-slide');
    slides.forEach(slide => {
      const collection = slide.dataset.collection;
      if (collection !== filterValue) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden', 'true');
      }
    });

    // Reinitialize swiper with correct slides
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.initSwiper();
    }
  }

  handleFilterClick(filterBtn) {
    const filterValue = filterBtn.dataset.filter;

    this.filters.forEach(f => f.classList.remove('products-list__filter--active'));
    filterBtn.classList.add('products-list__filter--active');

    this.filterProducts(filterValue);
  }

  handleDropdownChange(filterValue) {
    this.filterProducts(filterValue);
  }

  filterProducts(filterValue) {
    const visibleSlides = [];

    // Hide/show slides based on filter
    this.productCards.forEach(card => {
      const cardCollections = card.dataset.collection;
      const slide = card.closest('.swiper-slide');

      if (filterValue === 'all') {
        card.removeAttribute('data-hidden');
        if (slide) {
          slide.removeAttribute('data-hidden');
          slide.style.display = '';
          visibleSlides.push(slide);
        }
      } else {
        if (cardCollections && cardCollections.includes(filterValue)) {
          card.removeAttribute('data-hidden');
          if (slide) {
            slide.removeAttribute('data-hidden');
            slide.style.display = '';
            visibleSlides.push(slide);
          }
        } else {
          card.setAttribute('data-hidden', 'true');
          if (slide) {
            slide.setAttribute('data-hidden', 'true');
            slide.style.display = 'none';
          }
        }
      }
    });

    // Destroy and recreate swiper to properly recalculate layout
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;

      // Cascade animation
      visibleSlides.forEach(slide => slide.classList.add('is-hidden'));

      requestAnimationFrame(() => {
        this.initSwiper();

        // Reveal slides one by one
        visibleSlides.forEach((slide, index) => {
          setTimeout(() => {
            slide.classList.remove('is-hidden');
          }, index * 40);
        });
      });
    }
  }

  initAddToCart() {
    const addToCartButtons = this.section.querySelectorAll('.product-card__cta');

    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = button.dataset.variantId;

        if (!variantId) return;

        button.disabled = true;
        button.textContent = window.theme?.strings?.addingShort || 'Ajout...';

        try {
          await this.addToCart(variantId);

          button.textContent = window.theme?.strings?.added || 'Ajouté !';

          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));

          this.openQuickAdd(button.dataset.productId);

          setTimeout(() => {
            button.disabled = false;
            button.textContent = window.theme?.strings?.addToCart || 'Ajouter au panier';
          }, 2000);
        } catch (error) {
          console.error('Add to cart error:', error);
          button.disabled = false;
          button.textContent = window.theme?.strings?.error || 'Erreur';

          setTimeout(() => {
            button.textContent = window.theme?.strings?.addToCart || 'Ajouter au panier';
          }, 2000);
        }
      });
    });
  }

  async addToCart(variantId) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add to cart');
    }

    return await response.json();
  }

  openQuickAdd(productId) {
    const event = new CustomEvent('quick-add:open', {
      detail: { productId },
      bubbles: true
    });
    document.dispatchEvent(event);
  }
}

customElements.define('products-list', ProductsList);

document.addEventListener('DOMContentLoaded', () => {
  const productsListSections = document.querySelectorAll('.products-list');

  productsListSections.forEach(section => {
    const customElement = document.createElement('products-list');
    section.appendChild(customElement);
  });
});
